import { useState, useEffect } from 'react';
import { Navbar } from '../components/Navbar';
import { CardContainer } from '../components/CardContainer';
import { PrimaryButton } from '../components/PrimaryButton';
import {
  getClinicBillingInfo,
  saveClinicBillingInfo,
  getClinicPreferences,
  saveClinicPreferences,
  TIMEZONE_OPTIONS,
} from '../services/api';

const inputClass =
  'w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-base text-[#101822] placeholder-slate-400 outline-none transition-all focus:border-primary focus:ring-4 focus:ring-primary/10';

export const Settings = () => {
  const [taxNit, setTaxNit] = useState('');
  const [legalName, setLegalName] = useState('');
  const [billingEmail, setBillingEmail] = useState('');
  const [address, setAddress] = useState('');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Timezone is saved on its own, so a failure here never blocks the billing form.
  const [timezone, setTimezone] = useState('');
  const [tzLoading, setTzLoading] = useState(true);
  const [tzSaving, setTzSaving] = useState(false);
  const [tzError, setTzError] = useState<string | null>(null);
  const [tzSaved, setTzSaved] = useState(false);

  useEffect(() => {
    let active = true;
    getClinicBillingInfo()
      .then((info) => {
        if (!active || !info) return;
        setTaxNit(info.tax_nit ?? '');
        setLegalName(info.legal_name ?? '');
        setBillingEmail(info.billing_email ?? '');
        setAddress(info.address ?? '');
      })
      .catch((err) => {
        if (active) setError(err instanceof Error ? err.message : 'No se pudo cargar la información');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    getClinicPreferences()
      .then((prefs) => {
        if (active) setTimezone(prefs.timezone);
      })
      .catch((err) => {
        if (active) setTzError(err instanceof Error ? err.message : 'No se pudo cargar la zona horaria');
      })
      .finally(() => {
        if (active) setTzLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const handleTimezoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTzSaving(true);
    setTzError(null);
    setTzSaved(false);
    try {
      await saveClinicPreferences({ timezone });
      setTzSaved(true);
    } catch (err) {
      setTzError(err instanceof Error ? err.message : 'No se pudo guardar la zona horaria');
    } finally {
      setTzSaving(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await saveClinicBillingInfo({
        tax_nit: taxNit.trim(),
        legal_name: legalName.trim(),
        billing_email: billingEmail.trim(),
        address: address.trim(),
      });
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo guardar la información');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex min-h-screen w-full flex-col p-6 bg-[#f6f7f8]">
      <Navbar showLogout={true} />

      <main className="flex flex-1 flex-col items-center justify-start py-6">
        <div className="w-full max-w-2xl">
          <div className="mb-8">
            <h1 className="text-3xl font-extrabold tracking-tight text-[#101822]">Configuración</h1>
            <p className="mt-1 text-slate-500">Administra los datos de tu clínica.</p>
          </div>

          <CardContainer>
            <div className="mb-6">
              <h2 className="text-xl font-bold text-[#101822]">Datos de facturación</h2>
              <p className="mt-1 text-sm text-slate-500">
                Se usan para emitir la factura electrónica (FEL) de tu suscripción. Si los dejas en
                blanco, se factura como Consumidor Final.
              </p>
            </div>

            {loading ? (
              <div className="py-8 text-center text-slate-400">Cargando…</div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label htmlFor="taxNit" className="mb-1.5 block text-sm font-semibold text-slate-700">
                    NIT
                  </label>
                  <input
                    id="taxNit"
                    type="text"
                    value={taxNit}
                    onChange={(e) => setTaxNit(e.target.value)}
                    placeholder="ej. 1234567-8"
                    className={inputClass}
                  />
                </div>

                <div>
                  <label htmlFor="legalName" className="mb-1.5 block text-sm font-semibold text-slate-700">
                    Razón social / Nombre
                  </label>
                  <input
                    id="legalName"
                    type="text"
                    value={legalName}
                    onChange={(e) => setLegalName(e.target.value)}
                    placeholder="ej. Centro de Visión Plus, S.A."
                    className={inputClass}
                  />
                </div>

                <div>
                  <label htmlFor="billingEmail" className="mb-1.5 block text-sm font-semibold text-slate-700">
                    Correo de facturación
                  </label>
                  <input
                    id="billingEmail"
                    type="email"
                    value={billingEmail}
                    onChange={(e) => setBillingEmail(e.target.value)}
                    placeholder="facturacion@tuclinica.com"
                    className={inputClass}
                  />
                  <p className="mt-1.5 text-xs text-slate-400">
                    A esta dirección enviamos la factura de tu suscripción.
                  </p>
                </div>

                <div>
                  <label htmlFor="address" className="mb-1.5 block text-sm font-semibold text-slate-700">
                    Dirección
                  </label>
                  <input
                    id="address"
                    type="text"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="ej. 5a Avenida 10-25, Zona 1"
                    className={inputClass}
                  />
                </div>

                {error && (
                  <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
                    {error}
                  </div>
                )}
                {saved && !error && (
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                    Guardado.
                  </div>
                )}

                <div className="flex justify-end pt-2">
                  <PrimaryButton type="submit" disabled={saving}>
                    {saving ? 'Guardando…' : 'Guardar cambios'}
                  </PrimaryButton>
                </div>
              </form>
            )}
          </CardContainer>

          <div className="mt-6">
            <CardContainer>
              <div className="mb-6">
                <h2 className="text-xl font-bold text-[#101822]">Zona horaria</h2>
                <p className="mt-1 text-sm text-slate-500">
                  Define el reloj con el que se miden tus reportes: el corte de los ingresos del
                  mes, las visitas de hoy y la fecha de los pacientes que importes.
                </p>
              </div>

              {tzLoading ? (
                <div className="py-8 text-center text-slate-400">Cargando…</div>
              ) : (
                <form onSubmit={handleTimezoneSubmit} className="space-y-5">
                  <div>
                    <label
                      htmlFor="timezone"
                      className="mb-1.5 block text-sm font-semibold text-slate-700"
                    >
                      Zona horaria de la clínica
                    </label>
                    <select
                      id="timezone"
                      value={timezone}
                      onChange={(e) => setTimezone(e.target.value)}
                      className={inputClass}
                    >
                      {/* A zone saved outside this list stays selectable instead of silently
                          switching to the first option. */}
                      {!TIMEZONE_OPTIONS.some((o) => o.value === timezone) && timezone && (
                        <option value={timezone}>{timezone}</option>
                      )}
                      {TIMEZONE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {tzError && (
                    <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
                      {tzError}
                    </div>
                  )}
                  {tzSaved && !tzError && (
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                      Guardado. Los reportes ya usan esta zona horaria.
                    </div>
                  )}

                  <div className="flex justify-end pt-2">
                    <PrimaryButton type="submit" disabled={tzSaving}>
                      {tzSaving ? 'Guardando…' : 'Guardar zona horaria'}
                    </PrimaryButton>
                  </div>
                </form>
              )}
            </CardContainer>
          </div>
        </div>
      </main>
    </div>
  );
};
